/**
 * Bucket a list of timestamped items into Today / Yesterday /
 * This week / Earlier groups. Stable order, drops empty buckets.
 *
 * Boundaries:
 *   Today      → from local midnight today
 *   Yesterday  → from local midnight yesterday
 *   This week  → from the most recent Monday (local midnight)
 *   Earlier    → everything older
 *
 * "Earlier" gets a date label (e.g. "Mar 5") so the parent can still
 * orient themselves; same-day items collapse into one bucket.
 */

export interface DayBucket<T> {
  key: string;
  label: string;
  items: T[];
}

function startOfLocalDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function startOfWeek(d: Date): number {
  // Monday-first to match the mobile design's "this week" framing.
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0=Sun, 1=Mon...
  const offset = (dow + 6) % 7;
  x.setDate(x.getDate() - offset);
  return x.getTime();
}

function earlierBucketKey(at: number): {key: string; label: string} {
  const d = new Date(at);
  const label = d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
  // Key by local-day-start so all entries on the same day collapse.
  return {key: `earlier-${startOfLocalDay(d)}`, label};
}

export function groupByDay<T extends {at: number}>(items: T[], now = Date.now()): DayBucket<T>[] {
  const today = startOfLocalDay(new Date(now));
  const yesterday = today - 24 * 3600 * 1000;
  const thisWeek = startOfWeek(new Date(now));

  const today$: T[] = [];
  const yesterday$: T[] = [];
  const thisWeek$: T[] = [];
  const earlier = new Map<string, {label: string; items: T[]}>();

  for (const it of items) {
    if (it.at >= today) {
      today$.push(it);
    } else if (it.at >= yesterday) {
      yesterday$.push(it);
    } else if (it.at >= thisWeek) {
      thisWeek$.push(it);
    } else {
      const {key, label} = earlierBucketKey(it.at);
      const bucket = earlier.get(key) ?? {label, items: []};
      bucket.items.push(it);
      earlier.set(key, bucket);
    }
  }

  const out: DayBucket<T>[] = [];
  if (today$.length > 0) out.push({key: 'today', label: 'Today', items: today$});
  if (yesterday$.length > 0) out.push({key: 'yesterday', label: 'Yesterday', items: yesterday$});
  if (thisWeek$.length > 0) out.push({key: 'this-week', label: 'This week', items: thisWeek$});
  // Earlier buckets sorted newest-first by key (key has a timestamp suffix).
  const earlierKeys = Array.from(earlier.keys()).sort().reverse();
  for (const k of earlierKeys) {
    const b = earlier.get(k)!;
    out.push({key: k, label: b.label, items: b.items});
  }
  return out;
}

/**
 * Render an absolute timestamp as a short, parent-readable "when"
 * string for Activity rows. Today → time of day, this week → weekday
 * name, older → short date.
 */
export function formatWhen(at: number, now = Date.now()): string {
  const d = new Date(at);
  const today = startOfLocalDay(new Date(now));
  const yesterday = today - 24 * 3600 * 1000;
  const thisWeek = startOfWeek(new Date(now));
  if (at >= today) {
    return d.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'});
  }
  if (at >= yesterday) {
    return 'Yesterday';
  }
  if (at >= thisWeek) {
    return d.toLocaleDateString(undefined, {weekday: 'short'});
  }
  return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}
